<template>
  <div class="props-editor flex flex-col">
    <div class="flex-shrink-0">
      <div class="mb-4 mt-4" v-if="isDrawingMode">
        <a-tooltip placement="bottom" :title="t('common.exitBrushModeTip')">
          <a-button class="full-width" danger @click="exitDrawingMode" shape="round">
            <LogoutOutlined /> {{ t('common.exit') }}
            {{
              appState.isAiInpaintMode
                ? t('common.aieraser')
                : appState.isInpaintMode
                  ? t('common.eraser')
                  : appState.isDraw2CancelTransMode
                    ? t('common.cancelTranslateBrush')
                    : t('common.brush')
            }}
            {{ t('common.mode') }}
          </a-button>
        </a-tooltip>
      </div>
      <a-tabs v-model:activeKey="activeKey" size="small">
        <a-tab-pane key="config" v-if="isShow">
          <template #tab>
            <span class="flex items-center">
              <SettingOutlined />{{ t('common.setting') }}
            </span>
          </template>
        </a-tab-pane>
        <a-tab-pane key="layer">
          <template #tab>
            <div class="flex items-center">
              <span class="i-fluent:layer-24-filled text-4.5 mr-2"></span>{{ t('common.layer') }}
            </div>
          </template>
        </a-tab-pane>
        <a-tab-pane key="preview">
          <template #tab>
            <div class="flex items-center">
              <EyeOutlined class="mr-2" />{{ t('common.preview') }}
            </div>
          </template>
        </a-tab-pane>
      </a-tabs>
    </div>
    <div class="flex-grow">
      <!-- {{ formValue?.fontStyle }}== {{ formValue?.fg }}== -->
      <!-- {{ appState.currentPropsEditorType }} -->
      <!-- {{ selectedObjectProps }} -->
      <div v-if="isShow && activeKey === 'config'">
        <!-- {{ globalData }}== -->
        <SchemaForm :key="appState.selectedObjectUid" :schema="schema" :modelValue="formValue" :globalData="globalData"
          @update:modelValue="onPropsChange" @component-event="onComponentEvent"></SchemaForm>
        <div class="extra-actions" v-if="!isDrawingMode">
          <a-button class="full-width" @click="doDel" shape="round">
            <DeleteOutlined />{{ t('common.delete') }}
          </a-button>
        </div>
      </div>
      <Layers v-else-if="activeKey === 'layer'"></Layers>
      <ImagePreview v-else-if="activeKey === 'preview'"></ImagePreview>
    </div>
  </div>
</template>
<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import Layers from '@/components/Layers/Index.vue';
import ImagePreview from '@/components/ImagePreview/Index.vue';
import { DeleteOutlined, LogoutOutlined, SettingOutlined, EyeOutlined } from '@ant-design/icons-vue';
import schemas from './schemas';
import { getGroupingObjects, uppercaseByWords, uppercaseBySentence } from '@/utils';
import { sendLog } from '@/utils/sentry';
import i18n from '@/locales/i18n';

const { t, locale } = i18n.global;
const store = useStore();
const appState = computed(() => store.state.app);
const selectedObject = computed(() => store.getters['app/selectedObject']);
const selectedObjectUid = computed(() => appState.value.selectedObjectUid);
const isDrawingMode = computed(() => appState.value.isDrawingMode);
const selectedObjectProps = computed(() => store.getters['app/selectedObjectProps']);
const isShow = computed(() => {
  return (
    (selectedObject.value && ['textbox', 'rect', 'image'].indexOf(selectedObject.value.type) > -1) ||
    isDrawingMode.value
  );
});

const globalData = computed(() => {
  return {
    targetLanguage: appState.value.targetLanguage,
  };
});

const groupProps = computed(() => {
  if (!appState.value.isGrouping) return;
  let { objects } = getGroupingObjects();
  // 把store里的map映射出来，类似fgType这些变量存在map里
  objects = objects.map(i => ({ ...i, ...appState.value.objectPropsMap[i.$$uid] }));
  if (objects.length < 2) return;
  const {
    fontStyle,
    fontWeight = '',
    fgType,
    fgGradientDirection,
    fgGradientStart,
    fgGradientEnd,
    textAlign,
    fg,
    backgroundColor,
    fontSize,
    fontFamily,
    lineHeight,
  } = objects[0];

  // props里的fontStyle变成数组了
  const $fontStyle = (Array.isArray(fontStyle) ? fontStyle[1] : fontStyle) || '';
  const result = {
    fontSize,
    fontStyle: [fontWeight, $fontStyle],
    textAlign,
    fg,
    fontFamily,
    backgroundColor,
    lineHeight,
    fgType,
    fgGradientDirection,
    fgGradientStart,
    fgGradientEnd,
  };
  // 将第一个文本与后面其他文本做属性比较，不一致的不设置属性值
  objects.slice(1).forEach(object => {
    if (result['fg'] !== object['fg']) {
      result['fg'] = -1;
    }
    ['fontSize', 'fontFamily', 'fgType', 'fgGradientDirection', 'fgGradientStart', 'fgGradientEnd'].forEach(key => {
      if (result[key] !== object[key]) {
        result[key] = undefined;
      }
    });

    // -1表示多种字形字重
    if (result['fontStyle'][0] !== object['fontWeight']) {
      result['fontStyle'][0] = -1;
    }

    // props里的fontStyle变成数组了
    const $fontStyle = Array.isArray(object['fontStyle']) ? object['fontStyle'][1] : object['fontStyle'];
    if (result['fontStyle'][1] !== $fontStyle) {
      result['fontStyle'][1] = -1;
    }
    ['textAlign', 'backgroundColor', 'lineHeight'].forEach(key => {
      if (result[key] !== object[key]) {
        result[key] = -1;
      }
    });
  });

  return result;
});

const drawingProps = computed(() => appState.value.drawingConfig);

const activeKey = ref('layer');

watch(isShow, nv => {
  activeKey.value = nv ? 'config' : 'layer';
});

watch(selectedObjectUid, (nv, ov) => {
  if (nv >= 0 && nv !== ov) {
    if (['textbox', 'rect', 'image'].indexOf(store.getters['app/selectedObject']?.type) > -1) {
      activeKey.value = 'config';
    }
  }
  console.log(nv, ov);
});
const formValue = computed(() => {
  const { isGrouping, isDrawingMode } = appState.value;
  return isGrouping ? groupProps.value : isDrawingMode ? drawingProps.value : selectedObjectProps.value;
});

const onPropsChange = val => {
  // sendLog('editFromPropsEditor')
  const { isGrouping, isDrawingMode } = appState.value;
  if (isGrouping) {
    console.log('onPropsChange', val);
    store.dispatch('app/updateGroupingObjectFromPropsEditor', val);
  } else if (isDrawingMode) {
    store.commit('app/SET_DRAWING_CONFIG', val);
  } else {
    store.commit('app/UPDATE_SELECTED_OBJECT_PROPS', val);
  }
};

const doDel = () => {
  if (appState.value.isGrouping) {
    store.dispatch('app/deleteGroupObject');
    sendLog('deleteGroup');
  } else {
    store.dispatch('app/deleteSelectedObject');
    sendLog('deleteObject');
  }
};

const exitDrawingMode = () => {
  store.commit('app/SET_DRAWING_MODE', false);
  store.commit('app/SET_STATE', { isInpaintMode: false, isAiInpaintMode: false, isDraw2CancelTransMode: false });
};

/**
 * 这里从 helper 中取得 预设的schemas
 * 再根据 state 变化重新计算 schemas 
 */
const schema = computed(() => {
  const { currentPropsEditorType, isGrouping, isDrawingMode, isInpaintMode } = appState.value;
  const type = isGrouping ? 'group' : isInpaintMode ? 'inpaint' : isDrawingMode ? 'drawing' : currentPropsEditorType;
  console.log(123, type, selectedObjectProps.value, appState.value);
  try {
    const result = schemas[type](selectedObjectProps.value, t, locale.value, appState.value);
    console.log({ result: schemas })
    return result;
  } catch (err) {
    console.error(err);
    sendLog(`propsSchemaSwitchError:${type}-${isGrouping}-${isDrawingMode}-${currentPropsEditorType}`);
    return {};
  }
});

console.log({ "schema------init": schema })
watch(schema.value, (nv) => {
  console.log({ "schema--change": nv })
})

const onComponentEvent = e => {
  // console.log('onComponentEvent', e);
  if (e.name === 'input-blur') {
    sendLog('editText');
  }

  if (e.name === 'align-clicked') {
    store.dispatch('app/alignObjects', { align: e.value });
  }

  if (e.name === 'uppercase' || e.name === 'lowercase') {
    store.dispatch('app/updateGroupTextCase', {
      transformer: target => target[e.name === 'uppercase' ? 'toUpperCase' : 'toLowerCase'](),
    });
  }
  if (e.name === 'wordFirstLetterUppercase') {
    store.dispatch('app/updateGroupTextCase', {
      transformer: target => uppercaseByWords(target),
    });
  }
  if (e.name === 'sentenceFirstLetterUppercase') {
    store.dispatch('app/updateGroupTextCase', {
      transformer: target => uppercaseBySentence(target),
    });
  }

  if (e.name === 'button-clicked') {
    if (e.config.name === 'cancel-translate') {
      sendLog('cancelTranslate');
      store.dispatch('app/cancelTranslate', { props: selectedObjectProps.value, uid: selectedObjectUid.value });
    }
    if (e.config.name === 'retranslate') {
      sendLog('retranslate');
      store.dispatch('app/retranslate', { props: selectedObjectProps.value, uid: selectedObjectUid.value });
    }
  }
};
</script>
<style lang="less" scoped>
.props-editor {
  height: 100%;
}

.extra-actions {
  padding: 10px 6px;
}
</style>
